<script setup>
import Button from 'primevue/button'
import Divider from 'primevue/divider'
import Popover from 'primevue/popover'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLayoutWidth } from '@/core/composables/use-layout-width.composable'
import { useTheme } from '@/shared/composables/use-theme.composable'

const { t } = useI18n()
const { mode, setMode } = useTheme()
const { isFullWidth, toggle: toggleLayoutWidth } = useLayoutWidth()

const menu = ref()
const isOpen = ref(false)

const themeOptions = computed(() => [
  { value: 'system', icon: 'pi pi-desktop', label: t('core.header.theme.system') },
  { value: 'light', icon: 'pi pi-sun', label: t('core.header.theme.light') },
  { value: 'dark', icon: 'pi pi-moon', label: t('core.header.theme.dark') },
])

const currentThemeIcon = computed(
  () => themeOptions.value.find(option => option.value === mode.value)?.icon ?? 'pi pi-desktop',
)

function displayContextMenu(event) {
  menu.value.toggle(event)
}

function popoverShow() {
  isOpen.value = true
}

function popoverHide() {
  isOpen.value = false
}

function handleThemeClick(value) {
  if (value !== mode.value)
    setMode(value)

  menu.value?.hide()
}

function handleLayoutWidthClick() {
  toggleLayoutWidth()
  menu.value?.hide()
}
</script>

<template>
  <div class="relative flex items-center justify-center">
    <Button
      v-tooltip.bottom="t('core.header.appearance')"
      :icon="currentThemeIcon"
      class="w-11! h-11! p-0! bg-dss-surface-200 hover:bg-dss-surface-250 transition-colors duration-200 rounded-2xl border-0 focus:outline-none focus:ring-0 transform-gpu will-change-transform text-dss-secondary-500"
      :pt="{ label: { class: 'hidden' } }"
      :aria-label="t('core.header.theme.aria-label')"
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-controls="appearance-popover"
      data-testid="appearance-button"
      @click="displayContextMenu($event)"
    />

    <Popover
      id="appearance-popover"
      ref="menu"
      class="w-60 bg-white dark:bg-dss-surface-50 before:hidden after:hidden mt-2! rounded-2xl! border! border-dss-surface-200 shadow-[0px_4px_6px_-4px_#0000001A,0px_10px_15px_-3px_#0000001A] font-inter"
      :pt="{ content: { class: 'p-2' } }"
      @show="popoverShow"
      @hide="popoverHide"
    >
      <div class="flex flex-col gap-2">
        <div>
          <span class="block my-2 mx-3 text-dss-surface-850 text-xs font-medium uppercase leading-[normal]">
            {{ t('core.header.theme.title') }}
          </span>

          <ul>
            <li
              v-for="option in themeOptions"
              :key="option.value"
              class="group cursor-pointer"
              :data-testid="`theme-option-${option.value}`"
              @click="handleThemeClick(option.value)"
            >
              <div
                class="flex items-center gap-3 py-2 px-3 rounded-lg transition-colors duration-200"
                :class="[mode === option.value ? 'bg-dss-surface-100 dark:bg-dss-surface-100' : 'hover:bg-dss-surface-60']"
              >
                <i
                  :class="option.icon"
                  class="w-5 text-sm text-dss-surface-925"
                />
                <span class="flex-1 text-nowrap text-sm font-medium text-dss-surface-925">
                  {{ option.label }}
                </span>
                <i
                  v-if="mode === option.value"
                  class="pi pi-check text-xs text-dss-primary-600"
                />
              </div>
            </li>
          </ul>
        </div>

        <Divider class="my-0! -mx-2! w-auto!" />

        <div>
          <span class="block my-2 mx-3 text-dss-surface-850 text-xs font-medium uppercase leading-[normal]">
            {{ t('core.header.layout-title') }}
          </span>

          <button
            type="button"
            class="flex w-full cursor-pointer items-center gap-3 py-2 px-3 rounded-lg text-left transition-colors duration-200 hover:bg-dss-surface-60"
            data-testid="layout-width-option"
            @click="handleLayoutWidthClick"
          >
            <i
              :class="isFullWidth ? 'pi pi-arrow-down-left-and-arrow-up-right-to-center' : 'pi pi-expand'"
              class="w-5 text-sm text-dss-surface-925"
            />
            <span class="flex-1 text-sm font-medium text-dss-surface-925">
              {{ isFullWidth ? t('core.header.layout-compress') : t('core.header.layout-expand') }}
            </span>
          </button>
        </div>
      </div>
    </Popover>
  </div>
</template>
